// src/tabs/_paramsUi.jsx — primitives DA « Paramètres » (CHANTIER DA — Lot Paramètres).
//
// Patron §8 ter (DESIGN.md) — « fiche + accordéons » appliqué à l'onglet Paramètres
// (mapping §8 quinquies : Paramètres → Fiche + accordéons). Sous-onglets icône lucide
// + actif souligné corail ; sections en cartes/accordéons blanc franc avec pastille
// d'icône de TEINTE-CATÉGORIE FIXE (config = ardoise, §2) ; alertes en lignes fines
// à liséré gauche ; boutons primaire corail / secondaire blanc-marine.
//
// Local à l'espace « Paramètres » (ParamsPanel + ComptesPanel + Beds24SyncPanel).
// Présentation pure : zéro logique, zéro donnée, zéro persistance.

import { useState } from "react"
import { ChevronDown, AlertTriangle, AlertCircle, Info, CheckCircle2 } from "lucide-react"
import { DS } from "../lib/ui"

// Mapping VALIDÉ teinte-catégorie → token DS (§2). Constant et permanent.
export const CAT_TINTS = {
  ciel:    { bg: DS.catCielBg,    ico: DS.catCielIco },
  indigo:  { bg: DS.catIndigoBg,  ico: DS.catIndigoIco },
  corail:  { bg: DS.catCorailBg,  ico: DS.catCorailIco },
  ardoise: { bg: DS.catArdoiseBg, ico: DS.catArdoiseIco },
  rose:    { bg: DS.catRoseBg,    ico: DS.catRoseIco },
}

// Pastille d'icône de section (carrée douce, §8 quater). `icon` = composant lucide.
// Règle #14 : sur en-tête OUVERT (fond marine plein), la pastille passe en
// verre dépoli blanc + icône blanche (lisibilité) ; FERMÉ → teinte-catégorie.
export function CatPastille({ tint = "ardoise", icon: IconCmp, size = 30, open = false }) {
  const t = CAT_TINTS[tint] || CAT_TINTS.ardoise
  return (
    <span
      aria-hidden="true"
      style={{
        flex: "0 0 auto", width: size, height: size, borderRadius: 8,
        background: open ? "rgba(255,255,255,.18)" : t.bg,
        color: open ? "#fff" : t.ico,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {IconCmp && <IconCmp size={Math.round(size * 0.57)} strokeWidth={1.8} />}
    </span>
  )
}

// SubTabNav — barre de sous-onglets DA : icône lucide trait fin + label, actif
// souligné corail (même motif que la nav fiche logement §8 ter). `flexWrap` pour
// rester lisible à 375px (responsive Lot 0 acquis).
// items = [{ id, label, icon }]. `active` = id courant. `onSelect(id)`.
export function SubTabNav({ items, active, onSelect }) {
  return (
    <div
      style={{
        display: "flex", flexWrap: "wrap", gap: 2,
        background: DS.card, borderRadius: DS.radiusCard, padding: "2px 6px",
        marginBottom: 16, border: `0.5px solid ${DS.border}`,
        boxShadow: "0 1px 3px rgba(33,83,135,.05)",
      }}
    >
      {items.map((t) => {
        const isActive = t.id === active
        const Ico = t.icon
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(t.id)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "10px 14px", border: "none",
              borderBottom: `2px solid ${isActive ? DS.corail : "transparent"}`,
              background: "transparent",
              color: isActive ? DS.corail : DS.textSecondary,
              fontWeight: isActive ? 600 : 500,
              fontSize: 12.5, cursor: "pointer", fontFamily: DS.fontBody,
            }}
          >
            {Ico && <Ico size={15} strokeWidth={isActive ? 2 : 1.8} />} {t.label}
          </button>
        )
      })}
    </div>
  )
}

// Section — carte/accordéon blanc franc (PAS de fond teinté décoratif, §7) avec
// pastille teinte-catégorie FIXE. Repliable, ouverte par défaut (les réglages
// restent visibles ; on peut replier le bruit). En-tête = chevron + pastille +
// titre Fraunces + sous-titre gris court. `title` reste le seul texte du nœud
// titre (compat tests getByText).
export function Section({ tint = "ardoise", icon, title, subtitle, defaultOpen = true, headerRight, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div
      className="fbz-card"
      style={{
        background: DS.card,
        border: `0.5px solid ${DS.border}`,
        borderRadius: DS.radiusCard,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: open ? DS.marine : DS.card }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{
            display: "flex", alignItems: "center", gap: 11,
            flex: 1, minWidth: 0, textAlign: "left", cursor: "pointer",
            background: "transparent", border: "none",
            padding: "14px 4px 14px 18px", fontFamily: DS.fontBody,
          }}
        >
          <ChevronDown
            size={18}
            strokeWidth={2}
            aria-hidden="true"
            style={{
              flex: "0 0 auto", color: open ? "#fff" : DS.textMuted,
              transition: `transform ${DS.durBase}`,
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          />
          <CatPastille tint={tint} icon={icon} open={open} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: "block",
                fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 16,
                color: open ? "#fff" : DS.marineDark, lineHeight: 1.2,
              }}
            >
              {title}
            </span>
            {subtitle && (
              <span style={{ display: "block", fontSize: 12.5, color: open ? "rgba(255,255,255,.7)" : DS.textSecondary, marginTop: 3, lineHeight: 1.45 }}>
                {subtitle}
              </span>
            )}
          </span>
        </button>
        {headerRight && <div style={{ flex: "0 0 auto", paddingRight: 14 }}>{headerRight}</div>}
      </div>
      {open && <div style={{ padding: "4px 18px 18px" }}>{children}</div>}
    </div>
  )
}

// Sous-section interne d'une Section (titre Fraunces + séparateur hairline).
export function SubSection({ icon: IconCmp, title, intro, first = false, children }) {
  return (
    <div
      style={{
        marginTop: first ? 4 : 18,
        paddingTop: first ? 0 : 16,
        borderTop: first ? "none" : `1px solid ${DS.border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: intro ? 5 : 12 }}>
        {IconCmp && <IconCmp size={16} strokeWidth={1.8} aria-hidden="true" style={{ flex: "0 0 auto", color: DS.marine }} />}
        <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 14.5, color: DS.marineDark }}>{title}</span>
      </div>
      {intro && <div style={{ fontSize: 12, color: DS.textSecondary, marginBottom: 13, lineHeight: 1.5 }}>{intro}</div>}
      {children}
    </div>
  )
}

// FicheAlert — alerte en LIGNE FINE (liséré gauche 3px). Sens : warning (ambre) ·
// danger (rouge) · info (ciel) · success (vert). Présentation pure.
const ALERT_TONES = {
  warning: { bg: DS.warningBg, border: "#f1d9ac", accent: DS.warning, text: DS.warningText, Icon: AlertTriangle },
  danger:  { bg: DS.dangerBg,  border: "#f3c6c6", accent: DS.danger,  text: DS.dangerText,  Icon: AlertCircle },
  info:    { bg: DS.cielSoft,  border: "#c8e0f5", accent: DS.ciel,    text: DS.marineDeep,  Icon: Info },
  success: { bg: DS.successBg, border: "#bfe6d6", accent: DS.success, text: DS.successText, Icon: CheckCircle2 },
}
export function FicheAlert({ tone = "info", children, style, role, testId }) {
  const t = ALERT_TONES[tone] || ALERT_TONES.info
  const Ico = t.Icon
  return (
    <div
      role={role}
      data-testid={testId}
      style={{
        display: "flex", gap: 9, alignItems: "flex-start",
        padding: "9px 12px", borderRadius: 8, fontSize: 12.5, lineHeight: 1.45,
        background: t.bg, border: `0.5px solid ${t.border}`, borderLeft: `3px solid ${t.accent}`,
        color: t.text, fontFamily: DS.fontBody,
        ...style,
      }}
    >
      <Ico size={15} strokeWidth={1.9} aria-hidden="true" style={{ flex: "0 0 auto", marginTop: 1 }} />
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  )
}

// StatusPill — pastille de statut sémantique (fond tinté + ton sombre même famille).
const PILL_TONES = {
  success: { bg: DS.successBg, text: DS.successText },
  warning: { bg: DS.warningBg, text: DS.warningText },
  danger:  { bg: DS.dangerBg,  text: DS.dangerText },
  info:    { bg: DS.cielSoft,  text: DS.marineDeep },
  neutral: { bg: "#eef1f4",    text: DS.textSecondary },
}
export function StatusPill({ tone = "neutral", children, style, title }) {
  const t = PILL_TONES[tone] || PILL_TONES.neutral
  return (
    <span
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: t.bg, color: t.text, fontFamily: DS.fontBody,
        fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 7,
        whiteSpace: "nowrap", ...style,
      }}
    >
      {children}
    </span>
  )
}

// Bouton primaire DA (action clé) — corail plein, s'enfonce au clic (.fbz-press).
export function PrimaryButton({ children, onClick, disabled, type = "button", style, title }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={disabled ? undefined : "fbz-press"}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
        background: disabled ? "#cdd5df" : DS.corail,
        color: "#fff", border: "none", borderRadius: DS.radiusControl,
        padding: "10px 18px", fontSize: 13, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer", fontFamily: DS.fontBody,
        ...style,
      }}
    >
      {children}
    </button>
  )
}

// Bouton secondaire DA — blanc, contour fin, texte marine (ou danger).
export function SecondaryButton({ children, onClick, disabled, type = "button", tone = "marine", style, title }) {
  const color = tone === "danger" ? DS.danger : tone === "info" ? DS.marine : DS.marine
  const borderCol = tone === "danger" ? "#f0c4c4" : DS.borderHover
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={disabled ? undefined : "fbz-press"}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
        background: DS.card,
        color, border: `0.5px solid ${borderCol}`, borderRadius: DS.radiusControl,
        padding: "10px 16px", fontSize: 13, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer", fontFamily: DS.fontBody,
        ...style,
      }}
    >
      {children}
    </button>
  )
}

// Champ de formulaire DA : label gris 12px au-dessus + input au radiusControl.
export const daLabelStyle = {
  fontSize: 12, color: DS.textSecondary, marginBottom: 5, fontWeight: 600,
  display: "block", fontFamily: DS.fontBody,
}
export const daInputStyle = {
  border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl,
  padding: "9px 12px", width: "100%", fontSize: 13, outline: "none",
  fontFamily: DS.fontBody, background: DS.card, boxSizing: "border-box",
}
export function FieldLabel({ children, style }) {
  return <div style={{ ...daLabelStyle, ...style }}>{children}</div>
}
